import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, Grid, Typography, Chip, LinearProgress,
  Select, MenuItem, FormControl, InputLabel, Divider, Stack, Tabs, Tab,
  ToggleButton, ToggleButtonGroup, Button, Collapse, IconButton, Table,
  TableBody, TableCell, TableHead, TableRow, TablePagination, useTheme, useMediaQuery
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { useApi } from "../hooks/useApi.js";
import { formLabel } from "../lib/labels.js";
import { ComboPartners } from "../components/combos/index.js";
import { currentPeriod, type PartnerReduced } from "@kappa-v2/contracts";

const fmtEur = (v?: number) => `€ ${(v ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

const CURRENT_YEAR = new Date().getFullYear();
const PERIODS: string[] = [];
for (let y = 2024; y <= CURRENT_YEAR + 2; y++) {
  for (let m = 1; m <= 12; m++) PERIODS.push(`${String(m).padStart(2, "0")}/${y}`);
}

// Ordine: Proprietario → Cavallo → Pony (coerente con Statistiche).
const PARTNER_TYPE_KEYS = ["form.partnerType.owner", "form.partnerType.horse", "form.partnerType.pony"];
const TYPE_ORDER = ["owner", "horse", "pony", "other"];
const typeRank = (k?: string) => {
  const i = TYPE_ORDER.indexOf((k ?? "").split(".").pop() ?? "");
  return i === -1 ? 99 : i;
};

interface PeriodKpi {
  period: string; totalCredits: number; totalDebits: number; totalCosts: number;
  balance: number; settled: number; unsettled: number;
}
interface SummaryItem { name: string; description: string; value: number; serviceType: string }
interface SummaryRow { personId: string; period: string; partnerType: string; totalCredit: number; items: SummaryItem[] }

function SummaryCard({ title, value, icon, color, subtitle }: { title: string; value: string; icon: React.ReactNode; color: string; subtitle?: string }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</Typography>
          <Box sx={{ color, p: 0.5, borderRadius: 1, bgcolor: color + "15" }}>{icon}</Box>
        </Box>
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

function PartnerRow({ row, name, contact, ptLabel, svcLabel, expanded, onToggle, t, isMobile }: {
  row: SummaryRow; name: string; contact: string;
  ptLabel: (k: string) => string; svcLabel: (k: string) => string;
  expanded: boolean; onToggle: () => void; t: (k: string) => string; isMobile: boolean;
}) {
  if (isMobile) {
    return (
      <Box sx={{ borderBottom: "1px solid rgba(0,0,0,.06)" }}>
        <Box onClick={onToggle} sx={{
          display: "flex", alignItems: "flex-start", gap: 1, px: 2, py: 1.25, cursor: "pointer",
          "&:hover": { bgcolor: "rgba(0,0,0,.02)" }
        }}>
          <IconButton size="small" sx={{ p: 0.25, mt: 0.25 }}>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} color="primary.main">{name}</Typography>
            {contact && <Typography variant="caption" color="text.secondary" display="block">{contact}</Typography>}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              <Chip size="small" variant="outlined" label={ptLabel(row.partnerType)} sx={{ fontSize: "0.68rem" }} />
              <Chip size="small" variant="outlined" label={row.period} sx={{ fontSize: "0.68rem" }} />
            </Stack>
          </Box>
          <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ whiteSpace: "nowrap", mt: 0.25 }}>{fmtEur(row.totalCredit)}</Typography>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ px: 2, pb: 1.5, bgcolor: "rgba(0,0,0,.015)" }}>
            {row.items.map((it, i) => (
              <Box key={i} sx={{ py: 1, borderTop: "1px solid rgba(0,0,0,.06)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }}>{it.name}</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ whiteSpace: "nowrap" }}>{fmtEur(it.value)}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {svcLabel(it.serviceType)}{it.description ? ` · ${it.description}` : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }
  return (
    <Box sx={{ borderBottom: "1px solid rgba(0,0,0,.06)" }}>
      <Box onClick={onToggle} sx={{
        display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,.02)" }
      }}>
        <IconButton size="small" sx={{ p: 0.25 }}>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
        <Typography sx={{ flex: 2, minWidth: 160 }} variant="body2" fontWeight={700} color="primary.main">{name}</Typography>
        <Typography sx={{ flex: 1.4, minWidth: 120 }} variant="body2" color="text.secondary">{contact}</Typography>
        <Typography sx={{ flex: 1.2, minWidth: 110 }} variant="body2">{ptLabel(row.partnerType)}</Typography>
        <Typography sx={{ flex: 1, minWidth: 80 }} variant="body2" color="text.secondary">{row.period}</Typography>
        <Typography sx={{ width: 120, textAlign: "right" }} variant="body2" fontWeight={700} color="primary.main">{fmtEur(row.totalCredit)}</Typography>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 3, pb: 1.5, bgcolor: "rgba(0,0,0,.015)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 130 }}>{t("summary.colType")}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 200 }}>{t("summary.colName")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("summary.colDescription")}</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 110 }} align="right">{t("summary.colValue")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.items.map((it, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ color: "text.secondary" }}>{svcLabel(it.serviceType)}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{it.name}</TableCell>
                  <TableCell>{it.description}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600} color="primary.main">{fmtEur(it.value)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}

export function SummaryPage() {
  const { t } = useTranslation();
  const { call, openPdf, printPdf } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [period, setPeriod] = useState(currentPeriod());
  const [tab, setTab] = useState<"dashboard" | "detail">("dashboard");

  // Cruscotto
  const [kpi, setKpi] = useState<PeriodKpi | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState(false);

  // Dettaglio per socio
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [partnerType, setPartnerType] = useState("");
  const [socio, setSocio] = useState("");
  const [partners, setPartners] = useState<PartnerReduced[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const ptLabel = (k: string) => (k ? formLabel(k) : "—");
  const svcLabel = (k: string) => (k === "form.service.other" ? t("stats.extra") : formLabel(k));

  useEffect(() => {
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all", { silent: true })
      .then((r) => setPartners(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setPartners([]));
  }, [call]);

  const partnerById = useMemo(() => {
    const m = new Map<string, PartnerReduced>();
    partners.forEach((p) => p._id && m.set(String(p._id), p));
    return m;
  }, [partners]);

  // Cruscotto fetch
  const fetchKpi = useCallback(() => {
    setKpiLoading(true); setKpiError(false);
    call<PeriodKpi>(`/api/v1/payments/credits/statistics/${period.replace("/", "-")}`)
      .then(setKpi)
      .catch(() => { setKpiError(true); setKpi(null); })
      .finally(() => setKpiLoading(false));
  }, [call, period]);

  useEffect(() => { fetchKpi(); }, [fetchKpi]);

  // Dettaglio fetch
  const fetchDetail = useCallback(() => {
    setDetailLoading(true);
    const qs = new URLSearchParams();
    if (partnerType) qs.set("partnerType", partnerType);
    if (socio) qs.set("personId", socio);
    const q = qs.toString();
    call<SummaryRow[]>(`/api/v1/payments/credits/summary/${period.replace("/", "-")}${q ? `?${q}` : ""}`)
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]))
      .finally(() => setDetailLoading(false));
  }, [call, period, partnerType, socio]);

  useEffect(() => { if (tab === "detail") fetchDetail(); }, [tab, fetchDetail]);

  // Ricarica i dati della scheda attiva (cruscotto o dettaglio) rispettando i filtri correnti.
  const reload = useCallback(() => {
    if (tab === "detail") fetchDetail();
    else fetchKpi();
  }, [tab, fetchDetail, fetchKpi]);

  const detailRows = useMemo(() => {
    return rows
      .map((r) => ({ ...r, fullName: partnerById.get(r.personId)?.fullName ?? r.personId, contact: partnerById.get(r.personId)?.contact ?? "" }))
      .sort((a, b) => typeRank(a.partnerType) - typeRank(b.partnerType) || a.fullName.localeCompare(b.fullName, "it"));
  }, [rows, partnerById]);

  // Paginazione solo lato display: totale, "Espandi tutto" e PDF restano sul set completo.
  const pagedRows = useMemo(
    () => detailRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [detailRows, page, rowsPerPage]
  );
  // Riporta alla prima pagina quando cambiano filtri/periodo (cambia il numero di righe).
  useEffect(() => { setPage(0); }, [detailRows.length, partnerType, socio, period]);

  const pdfPath = () => {
    const qs = new URLSearchParams();
    if (partnerType) qs.set("partnerType", partnerType);
    if (socio) qs.set("personId", socio);
    const q = qs.toString();
    return `/api/v1/payments/credits/summary/${period.replace("/", "-")}/pdf${q ? `?${q}` : ""}`;
  };

  const allExpanded = detailRows.length > 0 && expanded.size === detailRows.length;
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(detailRows.map((r) => r.personId)));
  const toggleOne = (id: string) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const collectionRate = kpi && kpi.totalCredits > 0 ? Math.round((kpi.totalDebits / kpi.totalCredits) * 100) : 0;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <PageHeader
        title={t("summary.title")}
        subtitle={t("summary.subtitle")}
        onRefresh={reload}
        actions={
          <FormControl size="small" sx={{ minWidth: 130 }} data-tour="sm-period">
            <InputLabel>{t("common.period")}</InputLabel>
            <Select label={t("common.period")} value={period} onChange={(e) => setPeriod(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
              {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        }
      />

      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, flexShrink: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid rgba(0,0,0,.08)" }}>
          <Tab value="dashboard" label={t("summary.tabDashboard")} data-tour="sm-tab-dashboard" />
          <Tab value="detail" label={t("summary.tabDetail")} data-tour="sm-tab-detail" />
        </Tabs>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2, pb: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-tour="sm-content">
        {/* ─── CRUSCOTTO ─── */}
        {tab === "dashboard" && (
          <Box sx={{ overflowY: "auto" }}>
            {kpiLoading && <Box sx={{ mb: 2 }}><LinearProgress /></Box>}
            {!kpiLoading && (kpiError || !kpi) ? (
              <EmptyState title={t("summary.noData")} description={t("summary.noDataDesc", { period })} />
            ) : kpi && (
              <>
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}><SummaryCard title={t("summary.totalCredits")} value={fmtEur(kpi.totalCredits)} icon={<TrendingUpIcon />} color="#E65100" subtitle={t("summary.toCollect")} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><SummaryCard title={t("summary.collected")} value={fmtEur(kpi.totalDebits)} icon={<AccountBalanceIcon />} color="#2E7D32" subtitle={t("summary.paymentsReceived")} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><SummaryCard title={t("summary.costs")} value={fmtEur(kpi.totalCosts)} icon={<TrendingDownIcon />} color="#C62828" subtitle={t("summary.periodOutflows")} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><SummaryCard title={t("summary.netBalance")} value={fmtEur(kpi.balance)} icon={<ReceiptIcon />} color={kpi.balance >= 0 ? "#1565C0" : "#C62828"} subtitle={t("summary.inMinusOut")} /></Grid>
                </Grid>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{t("summary.collectionRate")}</Typography>
                      <Chip label={`${collectionRate}%`} color={collectionRate >= 80 ? "success" : collectionRate >= 50 ? "warning" : "error"} size="small" />
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(100, collectionRate)} sx={{ height: 10, borderRadius: 5, mb: 1 }} color={collectionRate >= 80 ? "success" : collectionRate >= 50 ? "warning" : "error"} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="text.secondary">{t("summary.collectedOf", { paid: fmtEur(kpi.totalDebits), total: fmtEur(kpi.totalCredits) })}</Typography>
                      <Typography variant="caption" color="text.secondary">{t("summary.remaining", { amount: fmtEur(kpi.totalCredits - kpi.totalDebits) })}</Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>{t("summary.receiptsDetail")}</Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}><Box sx={{ textAlign: "center", py: 2 }}><Typography variant="h4" fontWeight={700} color="success.main">{kpi.settled}</Typography><Typography variant="body2" color="text.secondary">{t("summary.settled")}</Typography></Box></Grid>
                      <Grid item xs={12} sm={4}><Box sx={{ textAlign: "center", py: 2 }}><Typography variant="h4" fontWeight={700} color="warning.main">{kpi.unsettled}</Typography><Typography variant="body2" color="text.secondary">{t("summary.unsettled")}</Typography></Box></Grid>
                      <Grid item xs={12} sm={4}><Box sx={{ textAlign: "center", py: 2 }}><Typography variant="h4" fontWeight={700} color="text.primary">{kpi.settled + kpi.unsettled}</Typography><Typography variant="body2" color="text.secondary">{t("summary.totalReceipts")}</Typography></Box></Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">{t("common.period")}: {kpi.period}</Typography>
                      <Typography variant="caption" color="text.secondary">{t("summary.netBalance")}: <strong>{fmtEur(kpi.balance)}</strong></Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        )}

        {/* ─── DETTAGLIO PER SOCIO ─── */}
        {tab === "detail" && (
          <>
            <Box sx={{ flexShrink: 0, mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <Box sx={{ minWidth: 260, flex: 1 }}>
                  <ComboPartners label={t("summary.socio")} value={socio} onChange={setSocio} />
                </Box>
                <ToggleButtonGroup exclusive size="small" color="primary" value={partnerType} onChange={(_, v) => v !== null && setPartnerType(v)} sx={{ flexWrap: "wrap" }}>
                  <ToggleButton value="">{t("common.all")}</ToggleButton>
                  {PARTNER_TYPE_KEYS.map((k) => <ToggleButton key={k} value={k}>{ptLabel(k)}</ToggleButton>)}
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => printPdf(pdfPath())} disabled={!detailRows.length}>{t("common.print")}</Button>
                  <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={() => openPdf(pdfPath())} disabled={!detailRows.length}>{t("common.exportPdf")}</Button>
                  <Button variant="text" size="small" startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />} onClick={toggleAll} disabled={!detailRows.length}>
                    {allExpanded ? t("summary.closeAll") : t("summary.expandAll")}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {detailLoading && <Box sx={{ mb: 2, flexShrink: 0 }}><LinearProgress /></Box>}

            {!detailLoading && detailRows.length === 0 ? (
              <EmptyState title={t("summary.noPartnerData")} />
            ) : (
              <Card sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* intestazione colonne — fissa (solo desktop) */}
                {!isMobile && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, bgcolor: "#F7F5F0", borderBottom: "1px solid rgba(0,0,0,.08)", flexShrink: 0 }}>
                    <Box sx={{ width: 30 }} />
                    <Typography sx={{ flex: 2, minWidth: 160 }} variant="caption" fontWeight={700} color="text.secondary">{t("summary.socio")}</Typography>
                    <Typography sx={{ flex: 1.4, minWidth: 120 }} variant="caption" fontWeight={700} color="text.secondary">{t("summary.contact")}</Typography>
                    <Typography sx={{ flex: 1.2, minWidth: 110 }} variant="caption" fontWeight={700} color="text.secondary">{t("summary.partnerType")}</Typography>
                    <Typography sx={{ flex: 1, minWidth: 80 }} variant="caption" fontWeight={700} color="text.secondary">{t("common.period")}</Typography>
                    <Typography sx={{ width: 120, textAlign: "right" }} variant="caption" fontWeight={700} color="text.secondary">{t("summary.total")}</Typography>
                  </Box>
                )}
                {/* righe scorribili */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {pagedRows.map((r) => (
                    <PartnerRow key={r.personId} row={r} name={r.fullName} contact={r.contact}
                      ptLabel={ptLabel} svcLabel={svcLabel} isMobile={isMobile}
                      expanded={expanded.has(r.personId)} onToggle={() => toggleOne(r.personId)} t={t} />
                  ))}
                </Box>
                <TablePagination
                  component="div"
                  count={detailRows.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage={t("summary.rowsPerPage")}
                  sx={{ flexShrink: 0, borderTop: "1px solid rgba(0,0,0,.08)" }}
                />
                <Box sx={{ px: 2, py: 1, borderTop: "1px solid rgba(0,0,0,.08)", display: "flex", justifyContent: "space-between", bgcolor: "#FAFAF8", flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary">{t("summary.peopleCount", { count: detailRows.length })}</Typography>
                  <Typography variant="caption" fontWeight={700}>{t("summary.total")}: {fmtEur(detailRows.reduce((s, r) => s + r.totalCredit, 0))}</Typography>
                </Box>
              </Card>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
